import getLogger from '../../../utils/getLogger';
import { cookieStorage } from './cookieStorage';
import {
  ACCESS_TOKEN_KEY,
  ID_TOKEN_KEY,
  OKTA_STORAGE_KEY,
  OKTA_TOKEN_STORAGE_CHANGED_EVENT,
} from './tokenStorage.constants';
import { getStorageForTokenKey, getStorageKey, TokenStorageKey } from './tokenStorage.utils';
import { StorageValue } from './storage.types';

type StoredTokenShape = OktaAccessToken | OktaIdToken;
type AccessToken = Pick<OktaAccessToken, 'accessToken' | 'expiresAt' | 'tokenType' | 'scopes'>;
type StoredTokenRecord = Partial<Record<'accessToken' | 'expiresAt' | 'tokenType' | 'scopes', unknown>>;

const logger = getLogger('Okta Token Storage');

const isValidAccessToken = (token: StoredTokenShape | null): token is OktaAccessToken => {
  if (!token || typeof token !== 'object') return false;
  const tokenRecord = token as StoredTokenRecord;

  return (
    typeof tokenRecord.accessToken === 'string' &&
    typeof tokenRecord.expiresAt === 'number' &&
    Number.isFinite(tokenRecord.expiresAt) &&
    typeof tokenRecord.tokenType === 'string' &&
    Array.isArray(tokenRecord.scopes)
  );
};

const dispatchTokenStorageChanged = (): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(OKTA_TOKEN_STORAGE_CHANGED_EVENT));
};

const toAccessToken = (token: StoredTokenShape | null): AccessToken | null => {
  if (!isValidAccessToken(token)) return null;
  return {
    accessToken: token.accessToken,
    expiresAt: token.expiresAt,
    tokenType: token.tokenType,
    scopes: token.scopes,
  };
};

const getTokenStorageValue = (tokenKey: TokenStorageKey): StorageValue => {
  const storage = getStorageForTokenKey(tokenKey);
  return storage.getItem(getStorageKey(tokenKey));
};

const setTokenStorageValue = (tokenKey: TokenStorageKey, value: string): void => {
  const storageKey = getStorageKey(tokenKey);

  if (tokenKey === ACCESS_TOKEN_KEY) {
    const parsedToken = parseStoredToken(value);
    const accessToken = toAccessToken(parsedToken);
    if (!accessToken) {
      removeTokenStorageValue(ACCESS_TOKEN_KEY);
      return;
    }

    getStorageForTokenKey(tokenKey).setItem(
      storageKey,
      JSON.stringify(accessToken),
      accessToken.expiresAt ? new Date(accessToken.expiresAt * 1000) : undefined,
    );
    return;
  }

  getStorageForTokenKey(tokenKey).setItem(storageKey, value);
};

const removeTokenStorageValue = (tokenKey: TokenStorageKey): void => {
  getStorageForTokenKey(tokenKey).removeItem(getStorageKey(tokenKey));
};

const parseStoredToken = (value: StorageValue): StoredTokenShape | null => {
  if (!value) return null;

  try {
    return JSON.parse(value) as StoredTokenShape;
  } catch (err) {
    return null;
  }
};

const normalizeTokenValue = (value: unknown): StorageValue => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      const parsedValue = JSON.parse(value) as StoredTokenShape;
      return JSON.stringify(parsedValue);
    } catch (err) {
      return value;
    }
  }

  return JSON.stringify(value);
};

const getStoredTokenObject = (tokenKey: TokenStorageKey): StoredTokenShape | null => {
  return parseStoredToken(getTokenStorageValue(tokenKey));
};

const getTokenStorageMap = (): Partial<Record<TokenStorageKey, StoredTokenShape>> => {
  const accessToken = getStoredTokenObject(ACCESS_TOKEN_KEY);
  const idToken = getStoredTokenObject(ID_TOKEN_KEY);

  return {
    ...(accessToken ? { [ACCESS_TOKEN_KEY]: accessToken } : {}),
    ...(idToken ? { [ID_TOKEN_KEY]: idToken } : {}),
  };
};

const writeTokenStorageMap = (value: string): void => {
  try {
    const parsedValue = JSON.parse(value) as Record<string, unknown>;
    const accessTokenValue = normalizeTokenValue(parsedValue[ACCESS_TOKEN_KEY]);
    const idTokenValue = normalizeTokenValue(parsedValue[ID_TOKEN_KEY]);

    if (accessTokenValue) {
      setTokenStorageValue(ACCESS_TOKEN_KEY, accessTokenValue);
    } else {
      removeTokenStorageValue(ACCESS_TOKEN_KEY);
    }

    if (idTokenValue) {
      setTokenStorageValue(ID_TOKEN_KEY, idTokenValue);
    } else {
      removeTokenStorageValue(ID_TOKEN_KEY);
    }

    dispatchTokenStorageChanged();
  } catch (error) {
    logger.error('Failed to parse token to store in token storage', error);
  }
};

export const customTokenStorage = {
  getItem(key: string): StorageValue {
    if (key === OKTA_STORAGE_KEY) {
      const tokenStorageMap = getTokenStorageMap();
      return Object.keys(tokenStorageMap).length ? JSON.stringify(tokenStorageMap) : null;
    }

    if (key === ACCESS_TOKEN_KEY || key === ID_TOKEN_KEY) {
      return getTokenStorageValue(key);
    }

    return cookieStorage.getItem(key) || null;
  },
  setItem(key: string, value: string) {
    if (key === OKTA_STORAGE_KEY) {
      writeTokenStorageMap(value);
      return;
    }

    if (key === ACCESS_TOKEN_KEY || key === ID_TOKEN_KEY) {
      const normalizedValue = normalizeTokenValue(value);
      if (normalizedValue) {
        setTokenStorageValue(key, normalizedValue);
      } else {
        removeTokenStorageValue(key);
      }

      dispatchTokenStorageChanged();
      return;
    }

    cookieStorage.setItem(key, value);
  },
  removeItem(key: string) {
    if (key === OKTA_STORAGE_KEY) {
      removeTokenStorageValue(ACCESS_TOKEN_KEY);
      removeTokenStorageValue(ID_TOKEN_KEY);
      dispatchTokenStorageChanged();
      return;
    }

    if (key === ACCESS_TOKEN_KEY || key === ID_TOKEN_KEY) {
      removeTokenStorageValue(key);
      dispatchTokenStorageChanged();
      return;
    }

    cookieStorage.removeItem(key);
  },
};

export const getAccessToken = (): OktaAccessToken | null => {
  const token = getStoredTokenObject(ACCESS_TOKEN_KEY);
  return token && 'accessToken' in token ? (token as OktaAccessToken) : null;
};

export const getIdToken = (): OktaIdToken | null => {
  const token = getStoredTokenObject(ID_TOKEN_KEY);
  return token && 'idToken' in token ? (token as OktaIdToken) : null;
};
